import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { formatDateBR, formatDateTimeBR } from '../utils/dateUtils';
import { useQuery } from '@tanstack/react-query';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { assinaturasService } from '../services/assinaturas.service';
import { formatCurrency } from '../utils/formatters';
import PageContainer from '../components/PageContainer';
import PageHeader from '../components/PageHeader';

export default function FaturaDetalhes() {
  const { subscriptionId, invoiceId } = useParams<{ subscriptionId: string; invoiceId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Detectar origem: se veio do faturamento ou da assinatura
  const veioDoFaturamento = (location.state as Record<string, unknown> | null)?.fromFaturamento || false;

  const { data: invoice, isLoading } = useQuery({
    queryKey: ['invoice', subscriptionId, invoiceId],
    queryFn: () => assinaturasService.getInvoice(subscriptionId!, Number(invoiceId!)),
    enabled: !!subscriptionId && !!invoiceId,
  });

  const handleDownloadPDF = async () => {
    if (!subscriptionId || !invoiceId) return;

    try {
      const blob = await assinaturasService.downloadInvoicePDF(subscriptionId, Number(invoiceId));
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${invoice?.invoice_number || 'fatura'}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Erro ao baixar PDF:', error);
      alert('Erro ao baixar PDF da fatura');
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { label: string; className: string }> = {
      PAID: { label: 'Pago', className: 'bg-green-100 text-green-800' },
      PENDING: { label: 'Pendente', className: 'bg-yellow-100 text-yellow-800' },
      OVERDUE: { label: 'Vencido', className: 'bg-red-100 text-red-800' },
      CANCELLED: { label: 'Cancelado', className: 'bg-gray-100 text-gray-800' },
    };

    const config = statusConfig[status] || { label: status, className: 'bg-gray-100 text-gray-800' };
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${config.className}`}>
        {config.label}
      </span>
    );
  };

  if (isLoading) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
            <p className="mt-4 text-gray-600">Carregando detalhes da fatura...</p>
          </div>
        </div>
      </PageContainer>
    );
  }

  if (!invoice) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <p className="text-gray-600 mb-4">Fatura não encontrada</p>
            <button
              onClick={() => navigate(veioDoFaturamento ? '/faturamento' : `/assinaturas/${subscriptionId}`)}
              className="btn-primary"
            >
              Voltar
            </button>
          </div>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title={`Fatura ${invoice.invoice_number}`}
        description="Detalhes da fatura"
        breadcrumbs={[
          veioDoFaturamento
            ? { label: 'Faturamento', href: '/faturamento' }
            : { label: 'Assinatura', href: `/assinaturas/${subscriptionId}` },
          { label: `Fatura ${invoice.invoice_number}` },
        ]}
        actions={
          <div className="flex items-center gap-3">
            {getStatusBadge(invoice.status)}
            <button
              onClick={handleDownloadPDF}
              className="btn-secondary flex items-center gap-2"
            >
              <ArrowDownTrayIcon className="h-5 w-5" />
              Baixar PDF
            </button>
          </div>
        }
      />


        {/* Informações Gerais */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-sm font-medium text-gray-500 mb-2">Data de Emissão</h3>
            <p className="text-lg font-semibold text-gray-900">
              {formatDateBR(invoice.issue_date)}
            </p>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-sm font-medium text-gray-500 mb-2">Data de Vencimento</h3>
            <p className="text-lg font-semibold text-gray-900">
              {formatDateBR(invoice.due_date)}
            </p>
          </div>

          {invoice.paid_at && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-sm font-medium text-gray-500 mb-2">Data de Pagamento</h3>
              <p className="text-lg font-semibold text-gray-900">
                {formatDateTimeBR(invoice.paid_at)}
              </p>
            </div>
          )}

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-sm font-medium text-gray-500 mb-2">Valor Total</h3>
            <p className="text-2xl font-semibold text-gray-900">
              {formatCurrency(invoice.total_amount)}
            </p>
          </div>
        </div>

        {/* Informações da Assinatura */}
        {invoice.subscription && (
          <div className="bg-white rounded-lg shadow p-6 mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Informações da Assinatura</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-gray-500">Nome da Assinatura</p>
                <p className="text-lg font-semibold text-gray-900">{invoice.subscription.name}</p>
              </div>
              {invoice.subscription.plan && (
                <div>
                  <p className="text-sm text-gray-500">Plano</p>
                  <p className="text-lg font-semibold text-gray-900">{invoice.subscription.plan.name}</p>
                </div>
              )}
              {invoice.subscription.company && (
                <div>
                  <p className="text-sm text-gray-500">Empresa</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {invoice.subscription.company.nome || invoice.subscription.company.razao_social}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Resumo de Valores */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Resumo de Valores</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div>
              <p className="text-sm text-gray-500 mb-1">Valor Base</p>
              <p className="text-xl font-semibold text-gray-900">{formatCurrency(invoice.base_amount)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">Vidas Extras</p>
              <p className="text-xl font-semibold text-gray-900">{formatCurrency(invoice.extra_lives_amount)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">Exames Extras</p>
              <p className="text-xl font-semibold text-gray-900">{formatCurrency(invoice.extra_exams_amount)}</p>
            </div>
            {(() => {
              const totalDesconto = invoice.items
                ?.filter(item => item.type === 'discount')
                .reduce((sum, item) => sum + Math.abs(Number(item.total_price)), 0) || 0;
              
              if (totalDesconto > 0) {
                return (
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Desconto Aplicado</p>
                    <p className="text-xl font-semibold text-green-600">-{formatCurrency(totalDesconto)}</p>
                  </div>
                );
              }
              return null;
            })()}
            <div className="border-t-2 border-primary-500 pt-2">
              <p className="text-sm text-gray-500 mb-1">Total</p>
              <p className="text-2xl font-bold text-primary-600">{formatCurrency(invoice.total_amount)}</p>
            </div>
          </div>
        </div>

        {/* Informações sobre Desconto */}
        {(() => {
          const totalDesconto = invoice.items
            ?.filter(item => item.type === 'discount')
            .reduce((sum, item) => sum + Math.abs(Number(item.total_price)), 0) || 0;
          
          if (totalDesconto > 0) {
            const cpfsCobertos = invoice.items
              ?.filter(item => item.type === 'discount')
              .map(item => {
                // Extrair número de CPFs cobertos da descrição
                const match = item.description.match(/(\d+)\s+CPF/);
                return match ? parseInt(match[1]) : 0;
              })
              .reduce((sum, val) => sum + val, 0) || 0;
            
            return (
              <div className="bg-green-50 border border-green-200 rounded-lg shadow p-6 mb-8">
                <h2 className="text-xl font-bold text-green-800 mb-4 flex items-center">
                  <span className="mr-2">💰</span>
                  Desconto de Exames Clínicos Inclusos
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-green-700 mb-1">Total Descontado</p>
                    <p className="text-2xl font-bold text-green-800">{formatCurrency(totalDesconto)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-green-700 mb-1">CPFs Cobertos</p>
                    <p className="text-2xl font-bold text-green-800">{cpfsCobertos} CPF{cpfsCobertos > 1 ? 's' : ''}</p>
                  </div>
                  <div>
                    <p className="text-sm text-green-700 mb-1">Benefício</p>
                    <p className="text-lg font-semibold text-green-800">Exames Clínicos Inclusos no Plano</p>
                  </div>
                </div>
                <p className="mt-4 text-sm text-green-700">
                  Os exames clínicos dos primeiros {cpfsCobertos} colaborador{cpfsCobertos > 1 ? 'es' : ''} (CPFs únicos) 
                  foram cobertos pelo seu plano de assinatura e não foram cobrados nesta fatura.
                </p>
              </div>
            );
          }
          return null;
        })()}

        {/* Informações sobre Ajustes (Agendamentos não realizados) */}
        {(() => {
          const ajustesItems = invoice.items?.filter(item => item.type === 'adjustment') || [];
          if (ajustesItems.length > 0) {
            const totalAjustes = ajustesItems.reduce((sum, item) => sum + Math.abs(Number(item.total_price)), 0);
            const totalAgendamentosNaoRealizados = ajustesItems.reduce((sum, item) => {
              // Extrair número de agendamentos não realizados da descrição
              const match = item.description.match(/(\d+)\s+agendamento/);
              return sum + (match ? parseInt(match[1]) : 0);
            }, 0);
            
            return (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg shadow p-6 mb-8">
                <h2 className="text-xl font-bold text-yellow-800 mb-4 flex items-center">
                  <span className="mr-2">⚠️</span>
                  Observações sobre Agendamentos
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-yellow-700 mb-1">Valor Não Cobrado</p>
                    <p className="text-2xl font-bold text-yellow-800">{formatCurrency(totalAjustes)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-yellow-700 mb-1">Agendamentos Não Realizados</p>
                    <p className="text-2xl font-bold text-yellow-800">{totalAgendamentosNaoRealizados} agendamento{totalAgendamentosNaoRealizados > 1 ? 's' : ''}</p>
                  </div>
                </div>
                <p className="mt-4 text-sm text-yellow-700">
                  Alguns agendamentos dos pedidos não foram realizados (colaborador não compareceu).
                </p>
                <p className="mt-2 text-sm text-yellow-700">
                  O valor não cobrado foi ajustado nesta fatura. Apenas agendamentos realizados são cobrados.
                </p>
              </div>
            );
          }
          return null;
        })()}

        {/* Itens da Fatura */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Itens da Fatura</h2>
          {invoice.items && invoice.items.length > 0 ? (
            <div className="space-y-6">
              {/* Agrupar itens por pedido */}
              {(() => {
                // Extrair número do pedido da descrição
                const extractPedidoId = (description: string): number | null => {
                  const match = description.match(/Pedido #(\d+)/);
                  return match ? parseInt(match[1]) : null;
                };

                // Agrupar itens por pedido
                const itemsPorPedido = new Map<number | string, typeof invoice.items>();
                const itemsSemPedido: typeof invoice.items = [];

                invoice.items.forEach((item) => {
                  const pedidoId = extractPedidoId(item.description);
                  if (pedidoId) {
                    if (!itemsPorPedido.has(pedidoId)) {
                      itemsPorPedido.set(pedidoId, []);
                    }
                    itemsPorPedido.get(pedidoId)!.push(item);
                  } else {
                    itemsSemPedido.push(item);
                  }
                });

                return (
                  <>
                    {/* Itens sem pedido (base, desconto geral) */}
                    {itemsSemPedido.length > 0 && (
                      <div className="mb-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-3">Itens Gerais</h3>
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Descrição</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Quantidade</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Valor Unitário</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {itemsSemPedido.map((item) => {
                                const isDiscount = item.type === 'discount';
                                const isAdjustment = item.type === 'adjustment';
                                const isNegative = Number(item.total_price) < 0;
                                return (
                                  <tr key={item.id} className={isDiscount ? 'bg-green-50' : isAdjustment ? 'bg-yellow-50' : ''}>
                                    <td className="px-6 py-4 text-sm">
                                      <div className={isDiscount ? 'font-semibold text-green-700' : isAdjustment ? 'font-semibold text-yellow-800' : 'text-gray-900'}>
                                        {item.description}
                                        {isDiscount && (
                                          <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                            Desconto
                                          </span>
                                        )}
                                        {isAdjustment && (
                                          <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                            Ajuste
                                          </span>
                                        )}
                                      </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.quantity}</td>
                                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDiscount ? 'text-green-700 font-semibold' : isAdjustment ? 'text-yellow-800 font-semibold' : 'text-gray-500'}`}>
                                      {isNegative ? '-' : ''}{formatCurrency(Math.abs(Number(item.unit_price)))}
                                    </td>
                                    <td className={`px-6 py-4 whitespace-nowrap text-sm font-semibold ${isDiscount ? 'text-green-700' : isAdjustment ? 'text-yellow-800' : 'text-gray-900'}`}>
                                      {isNegative ? '-' : ''}{formatCurrency(Math.abs(Number(item.total_price)))}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Itens agrupados por pedido */}
                    {Array.from(itemsPorPedido.entries()).map(([pedidoId, items]) => {
                      const totalPedido = items.reduce((sum, item) => sum + Number(item.total_price), 0);
                      const colaboradoresPorPedido = new Map<number, typeof items>();
                      const itemsAjuste = items.filter(item => item.type === 'adjustment');
                      
                      items.forEach((item) => {
                        if (item.colaborador_id) {
                          if (!colaboradoresPorPedido.has(item.colaborador_id)) {
                            colaboradoresPorPedido.set(item.colaborador_id, []);
                          }
                          colaboradoresPorPedido.get(item.colaborador_id)!.push(item);
                        }
                      });

                      return (
                        <div key={pedidoId} className="mb-6 border border-gray-200 rounded-lg overflow-hidden">
                          <div className="bg-primary-50 px-6 py-3 border-b border-gray-200">
                            <h3 className="text-lg font-semibold text-gray-900">
                              Pedido #{pedidoId}
                              <span className="ml-4 text-sm font-normal text-gray-600">
                                {colaboradoresPorPedido.size} colaborador{colaboradoresPorPedido.size > 1 ? 'es' : ''} • {items.length} item{items.length > 1 ? 's' : ''} • Total: {formatCurrency(totalPedido)}
                              </span>
                            </h3>
                          </div>
                          
                          {/* Colaboradores do pedido */}
                          {Array.from(colaboradoresPorPedido.entries()).map(([colaboradorId, itemsColab]) => {
                            const colaborador = itemsColab[0]?.colaborador;
                            const totalColab = itemsColab.reduce((sum, item) => sum + Number(item.total_price), 0);
                            
                            return (
                              <div key={colaboradorId} className="border-b border-gray-100 last:border-b-0">
                                <div className="bg-gray-50 px-6 py-3">
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <h4 className="font-semibold text-gray-900">{colaborador?.nome || 'Colaborador'}</h4>
                                      <div className="text-sm text-gray-600 mt-1">
                                        <span>CPF: {colaborador?.cpf || 'N/A'}</span>
                                        {(colaborador?.cargo?.nome || colaborador?.setor?.nome) && (
                                          <span className="ml-4">
                                            {colaborador?.cargo?.nome || ''}
                                            {colaborador?.cargo?.nome && colaborador?.setor?.nome ? ' - ' : ''}
                                            {colaborador?.setor?.nome || ''}
                                          </span>
                                        )}
                                        {colaborador?.data_admissao && (
                                          <span className="ml-4">
                                            Admissão: {formatDateBR(colaborador.data_admissao)}
                                            {itemsColab[0]?.exame_agendamento?.exame?.nome && (
                                              <span className="ml-2 text-gray-700 font-medium">
                                                ({itemsColab[0].exame_agendamento.exame.nome})
                                              </span>
                                            )}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    <div className="text-right">
                                      <div className="text-sm text-gray-500">Total do Colaborador</div>
                                      <div className="text-lg font-bold text-gray-900">{formatCurrency(totalColab)}</div>
                                    </div>
                                  </div>
                                </div>
                                
                                {/* Exames do colaborador */}
                                <div className="px-6 py-4">
                                  <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                      <tr>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Exame</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Clínica</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Data</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Atividade</th>
                                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Valor Unitário</th>
                                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                                      </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                      {itemsColab.map((item) => {
                                        const isCovered = item.type === 'covered';
                                        // Coluna Exame deve mostrar o exame clínico, não o tipo de exame
                                        const exameNome = item.exame_agendamento?.exame_clinico?.nome || item.exame_agendamento?.exame?.nome || 'N/A';
                                        
                                        return (
                                          <tr key={item.id} className={isCovered ? 'bg-green-50' : ''}>
                                            <td className="px-4 py-3 text-sm">
                                              <div className="font-medium text-gray-900">{exameNome}</div>
                                              {isCovered && (
                                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 mt-1">
                                                  Coberto pelo Plano
                                                </span>
                                              )}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-500">
                                              {item.exame_agendamento?.clinica?.nome || '-'}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-500">
                                              {item.exame_agendamento?.data_agendamento ? formatDateBR(item.exame_agendamento.data_agendamento) : '-'}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-500">
                                              {item.exame_agendamento?.variacao?.nome || '-'}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-right text-gray-500">
                                              {formatCurrency(Math.abs(Number(item.unit_price)))}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-right font-semibold text-gray-900">
                                              {isCovered ? (
                                                <span className="text-green-700">R$ 0,00 (Coberto)</span>
                                              ) : (
                                                formatCurrency(Math.abs(Number(item.total_price)))
                                              )}
                                            </td>
                                          </tr>
                                        );
                                      })}
                                      <tr className="bg-gray-50 font-semibold">
                                        <td colSpan={5} className="px-4 py-3 text-right text-sm text-gray-700">
                                          Subtotal:
                                        </td>
                                        <td className="px-4 py-3 text-right text-sm text-gray-900">
                                          {formatCurrency(totalColab)}
                                        </td>
                                      </tr>
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            );
                          })}
                          
                          {/* Itens de ajuste do pedido (sem colaborador específico) */}
                          {itemsAjuste.length > 0 && (
                            <div className="px-6 py-4 bg-yellow-50 border-t border-yellow-200">
                              <h4 className="font-semibold text-yellow-800 mb-3">Ajustes do Pedido</h4>
                              <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-yellow-100">
                                  <tr>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-yellow-800 uppercase">Descrição</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-yellow-800 uppercase">Quantidade</th>
                                    <th className="px-4 py-2 text-right text-xs font-medium text-yellow-800 uppercase">Valor Unitário</th>
                                    <th className="px-4 py-2 text-right text-xs font-medium text-yellow-800 uppercase">Total</th>
                                  </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                  {itemsAjuste.map((item) => {
                                    const isNegative = Number(item.total_price) < 0;
                                    return (
                                      <tr key={item.id} className="bg-yellow-50">
                                        <td className="px-4 py-3 text-sm font-semibold text-yellow-800">
                                          {item.description}
                                          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                            Ajuste
                                          </span>
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-500">{item.quantity}</td>
                                        <td className="px-4 py-3 text-sm text-right font-semibold text-yellow-800">
                                          {isNegative ? '-' : ''}{formatCurrency(Math.abs(Number(item.unit_price)))}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-right font-semibold text-yellow-800">
                                          {isNegative ? '-' : ''}{formatCurrency(Math.abs(Number(item.total_price)))}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {/* Total Geral */}
                    <div className="mt-6 bg-gray-50 rounded-lg p-6">
                      <div className="flex justify-between items-center">
                        <span className="text-lg font-semibold text-gray-700">TOTAL GERAL DA FATURA</span>
                        <span className="text-2xl font-bold text-gray-900">{formatCurrency(invoice.total_amount)}</span>
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          ) : (
            <p className="text-gray-500">Nenhum item encontrado nesta fatura.</p>
          )}
        </div>
    </PageContainer>
  );
}

